import { createStore, createEvent, createEffect, sample } from "effector";
import {
  CallsState,
  CallsApiResponse,
  CallFilters,
  CallResponse,
  CallResponseType,
  CreateIdeaPayload,
  CommentsApiResponse,
  CreateCommentPayload,
  CallGoalsApiResponse,
  CallGeneralDataApiResponse,
} from "../model/types";
import { callsService } from "../service/api";

export const fetchCallsRequested = createEvent<CallFilters | undefined>();
export const loadMoreCallsRequested = createEvent<CallFilters | undefined>();
export const fetchCallDetailRequested = createEvent<string>();
export const updateCallBookmark = createEvent<{
  callId: string;
  isBookmarked: boolean;
}>();
export const updateCallInterest = createEvent<{
  callId: string;
  isBookmarked: boolean;
}>();
export const createIdeaRequested = createEvent<CreateIdeaPayload>();
export const fetchCallCommentsRequested = createEvent<string>();
export const createCommentRequested = createEvent<CreateCommentPayload>();
export const fetchCallGoalsRequested = createEvent<string>();
export const fetchCallGeneralDataRequested = createEvent();

// Events for fetching counts separately
export const fetchAllCallsCountRequested = createEvent();
export const fetchForYouCallsCountRequested = createEvent();

// My calls events
export const fetchDeclaredInterestCallsRequested = createEvent<
  CallFilters | undefined
>();
export const fetchBookmarkedCallsRequested = createEvent<
  CallFilters | undefined
>();
export const fetchArchivedCallsRequested = createEvent<
  CallFilters | undefined
>();
export const loadMoreDeclaredInterestCallsRequested = createEvent<
  CallFilters | undefined
>();
export const loadMoreBookmarkedCallsRequested = createEvent<
  CallFilters | undefined
>();
export const loadMoreArchivedCallsRequested = createEvent<
  CallFilters | undefined
>();

export const fetchCallsFx = createEffect(
  async (filters?: CallFilters): Promise<CallsApiResponse> => {
    return await callsService.getAllCalls(filters);
  },
);

export const loadMoreCallsFx = createEffect(
  async (filters?: CallFilters): Promise<CallsApiResponse> => {
    return await callsService.getAllCalls(filters);
  },
);

export const fetchCallDetailFx = createEffect(
  async (id: string): Promise<CallResponse> => {
    return await callsService.getCallById(id);
  },
);

export const updateCallBookmarkFx = createEffect(
  async ({
    callId,
    isBookmarked,
  }: {
    callId: string;
    isBookmarked: boolean;
  }): Promise<CallResponse> => {
    const action = isBookmarked ? "add" : "remove";
    return await callsService.callAction(callId, "bookmark", action);
  },
);
export const updateCallInterestFx = createEffect(
  async ({
    callId,
    isBookmarked,
  }: {
    callId: string;
    isBookmarked: boolean;
  }): Promise<CallResponse> => {
    // console.log("updateCallInterestFx starting:", { callId, isBookmarked });
    const action = isBookmarked ? "add" : "remove";
    const result = await callsService.callAction(callId, "interest", action);
    // console.log("updateCallInterestFx completed:", result);
    return result;
  },
);

export const createIdeaFx = createEffect(
  async (payload: CreateIdeaPayload): Promise<any> => {
    return await callsService.createIdea(payload);
  },
);

export const fetchCallCommentsFx = createEffect(
  async (callId: string): Promise<CommentsApiResponse> => {
    return await callsService.getCallComments(callId);
  },
);

export const createCommentFx = createEffect(
  async (payload: CreateCommentPayload): Promise<any> => {
    return await callsService.createComment(payload);
  },
);

export const fetchCallGoalsFx = createEffect(
  async (callId: string): Promise<CallGoalsApiResponse> => {
    return await callsService.getCallGoals(callId);
  },
);

// Effects for fetching counts separately
export const fetchAllCallsCountFx = createEffect(
  async (): Promise<CallsApiResponse> => {
    return await callsService.getAllCalls({ page: 1 }); // Only fetch minimal data to get count
  },
);

export const fetchForYouCallsCountFx = createEffect(
  async (): Promise<CallsApiResponse> => {
    return await callsService.getAllCalls({ page: 1, type: "Eligible" }); // Only fetch minimal data to get count
  },
);

export const fetchCallGeneralDataRequestedFx = createEffect(
  async (): Promise<CallGeneralDataApiResponse> => {
    return await callsService.getCallGeneralData();
  },
);

// My calls effects
export const fetchDeclaredInterestCallsFx = createEffect(
  async (filters?: CallFilters): Promise<CallsApiResponse> => {
    return await callsService.getDeclaredInterestCalls(filters);
  },
);

export const fetchBookmarkedCallsFx = createEffect(
  async (filters?: CallFilters): Promise<CallsApiResponse> => {
    return await callsService.getBookmarkedCalls(filters);
  },
);

export const fetchArchivedCallsFx = createEffect(
  async (filters?: CallFilters): Promise<CallsApiResponse> => {
    return await callsService.getArchivedCalls(filters);
  },
);

export const loadMoreDeclaredInterestCallsFx = createEffect(
  async (filters?: CallFilters): Promise<CallsApiResponse> => {
    return await callsService.getDeclaredInterestCalls(filters);
  },
);

export const loadMoreBookmarkedCallsFx = createEffect(
  async (filters?: CallFilters): Promise<CallsApiResponse> => {
    return await callsService.getBookmarkedCalls(filters);
  },
);

export const loadMoreArchivedCallsFx = createEffect(
  async (filters?: CallFilters): Promise<CallsApiResponse> => {
    return await callsService.getArchivedCalls(filters);
  },
);

export const $callsStore = createStore<CallsState>({
  calls: [],
  selectedCall: null,
  loading: false,
  loadMore: false,
  error: null,
  filters: {
    search: "",
    sort: "created",
  },
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    total_count: 0,
  },
  // Initialize separate count tracking
  allCallsCount: 0,
  forYouCallsCount: 0,
  comments: [],
  commentsLoading: false,
  commentsError: null,
  createCommentLoading: false,
  createCommentError: null,
  // Call goals initial state
  callGoals: [],
  callGoalsLoading: false,
  callGoalsError: null,
  // My calls initial state
  declaredInterestCalls: [],
  declaredInterestLoading: false,
  declaredInterestLoadMore: false,
  declaredInterestError: null,
  declaredInterestPagination: {
    page: 1,
    limit: 20,
    total: 0,
    total_count: 0,
  },
  bookmarkedCalls: [],
  bookmarkedLoading: false,
  bookmarkedLoadMore: false,
  bookmarkedError: null,
  bookmarkedPagination: {
    page: 1,
    limit: 20,
    total: 0,
    total_count: 0,
  },
  archivedCalls: [],
  archivedLoading: false,
  archivedLoadMore: false,
  archivedError: null,
  archivedPagination: {
    page: 1,
    limit: 20,
    total: 0,
    total_count: 0,
  },
  callGeneralData: {
    total_calls: 0,
    total_calls_for_you: 0,
  },
});

$callsStore
  .on(fetchCallsRequested, (state) => ({
    ...state,
    loading: true,
    error: null,
  }))
  .on(fetchCallsFx.doneData, (state, response) => ({
    ...state,
    calls: response.data,
    loading: false,
    error: null,
    pagination: {
      ...state.pagination,
      total: response.count,
      total_count: response.total_count,
      page: 1,
    },
  }))
  .on(fetchCallsFx.failData, (state, error) => ({
    ...state,
    loading: false,
    error: error.message,
  }))
  .on(loadMoreCallsRequested, (state) => ({
    ...state,
    loadMore: true,
    error: null,
  }))
  .on(loadMoreCallsFx.doneData, (state, response) => ({
    ...state,
    calls: [...state.calls, ...response.data],
    loadMore: false,
    error: null,
    pagination: {
      ...state.pagination,
      page: state.pagination.page + 1,
      total: response.count,
      total_count: response.total_count,
    },
  }))
  .on(loadMoreCallsFx.failData, (state, error) => ({
    ...state,
    loadMore: false,
    error: error.message,
  }))
  .on(updateCallBookmark, (state, { callId, isBookmarked }) => ({
    ...state,
    calls: state.calls.map((call) =>
      call.id === callId ? { ...call, is_call_bookmarked: isBookmarked } : call,
    ),
    selectedCall:
      state.selectedCall?.id === callId
        ? { ...state.selectedCall, is_call_bookmarked: isBookmarked }
        : state.selectedCall,
  }))
  .on(updateCallInterest, (state, { callId, isBookmarked }) => ({
    ...state,
    calls: state.calls.map((call) =>
      call.id === callId
        ? { ...call, is_call_declared_interest: isBookmarked }
        : call,
    ),
    selectedCall:
      state.selectedCall?.id === callId
        ? { ...state.selectedCall, is_call_declared_interest: isBookmarked }
        : state.selectedCall,
  }))
  .on(updateCallInterestFx.doneData, (state, response) => {
    // console.log(
      // "Store: updateCallInterestFx.doneData handler triggered:",
      // response,
    // );
    return {
      ...state,
      calls: state.calls.map((call) =>
        call.id === response.id
          ? {
              ...call,
              is_call_declared_interest: response.is_call_declared_interest,
            }
          : call,
      ),
      selectedCall:
        state.selectedCall?.id === response.id
          ? {
              ...state.selectedCall,
              is_call_declared_interest: response.is_call_declared_interest,
            }
          : state.selectedCall,
    };
  })
  .on(updateCallInterestFx.failData, (state, error) => ({
    ...state,
    error: error.message,
  }))
  .on(fetchCallDetailRequested, (state) => ({
    ...state,
    loading: true,
    error: null,
  }))
  .on(fetchCallDetailFx.doneData, (state, call) => ({
    ...state,
    selectedCall: call,
    loading: false,
    error: null,
  }))
  .on(fetchCallDetailFx.failData, (state, error) => ({
    ...state,
    loading: false,
    error: error.message,
  }))
  .on(createIdeaRequested, (state) => ({
    ...state,
    loading: true,
    error: null,
  }))
  .on(createIdeaFx.doneData, (state, response) => ({
    ...state,
    loading: false,
    error: null,
  }))
  .on(createIdeaFx.failData, (state, error) => ({
    ...state,
    loading: false,
    error: error.message,
  }))
  .on(fetchCallCommentsRequested, (state) => ({
    ...state,
    commentsLoading: true,
    commentsError: null,
  }))
  .on(fetchCallCommentsFx.doneData, (state, response) => ({
    ...state,
    comments: response.data,
    commentsLoading: false,
    commentsError: null,
  }))
  .on(fetchCallCommentsFx.failData, (state, error) => ({
    ...state,
    commentsLoading: false,
    commentsError: error.message,
  }))
  .on(createCommentRequested, (state) => ({
    ...state,
    createCommentLoading: true,
    createCommentError: null,
  }))
  .on(createCommentFx.doneData, (state) => ({
    ...state,
    createCommentLoading: false,
    createCommentError: null,
  }))
  .on(createCommentFx.failData, (state, error) => ({
    ...state,
    createCommentLoading: false,
    createCommentError: error.message,
  }))
  // Call Goals handlers
  .on(fetchCallGoalsRequested, (state) => ({
    ...state,
    callGoalsLoading: true,
    callGoalsError: null,
  }))
  .on(fetchCallGoalsFx.doneData, (state, response) => ({
    ...state,
    callGoals: response.data,
    callGoalsLoading: false,
    callGoalsError: null,
  }))
  .on(fetchCallGoalsFx.failData, (state, error) => ({
    ...state,
    callGoalsLoading: false,
    callGoalsError: error.message,
  }))
  // Count fetching handlers
  .on(fetchAllCallsCountFx.doneData, (state, response) => ({
    ...state,
    allCallsCount: response.total_count,
  }))
  .on(fetchForYouCallsCountFx.doneData, (state, response) => ({
    ...state,
    forYouCallsCount: response.total_count,
  }))
  // Declared Interest Calls handlers
  .on(fetchDeclaredInterestCallsRequested, (state) => ({
    ...state,
    declaredInterestLoading: true,
    declaredInterestError: null,
  }))
  .on(fetchDeclaredInterestCallsFx.doneData, (state, response) => ({
    ...state,
    declaredInterestCalls: response.data,
    declaredInterestLoading: false,
    declaredInterestError: null,
    declaredInterestPagination: {
      ...state.declaredInterestPagination,
      total: response.count,
      total_count: response.total_count,
      page: 1,
    },
  }))
  .on(fetchDeclaredInterestCallsFx.failData, (state, error) => ({
    ...state,
    declaredInterestLoading: false,
    declaredInterestError: error.message,
  }))
  .on(loadMoreDeclaredInterestCallsRequested, (state) => ({
    ...state,
    declaredInterestLoadMore: true,
    declaredInterestError: null,
  }))
  .on(loadMoreDeclaredInterestCallsFx.doneData, (state, response) => ({
    ...state,
    declaredInterestCalls: [...state.declaredInterestCalls, ...response.data],
    declaredInterestLoadMore: false,
    declaredInterestError: null,
    declaredInterestPagination: {
      ...state.declaredInterestPagination,
      page: state.declaredInterestPagination.page + 1,
      total: response.count,
      total_count: response.total_count,
    },
  }))
  .on(loadMoreDeclaredInterestCallsFx.failData, (state, error) => ({
    ...state,
    declaredInterestLoadMore: false,
    declaredInterestError: error.message,
  }))
  // Bookmarked Calls handlers
  .on(fetchBookmarkedCallsRequested, (state) => ({
    ...state,
    bookmarkedLoading: true,
    bookmarkedError: null,
  }))
  .on(fetchBookmarkedCallsFx.doneData, (state, response) => ({
    ...state,
    bookmarkedCalls: response.data,
    bookmarkedLoading: false,
    bookmarkedError: null,
    bookmarkedPagination: {
      ...state.bookmarkedPagination,
      total: response.count,
      total_count: response.total_count,
      page: 1,
    },
  }))
  .on(fetchBookmarkedCallsFx.failData, (state, error) => ({
    ...state,
    bookmarkedLoading: false,
    bookmarkedError: error.message,
  }))
  // Archived Calls handlers
  .on(fetchArchivedCallsRequested, (state) => ({
    ...state,
    archivedLoading: true,
    archivedError: null,
  }))
  .on(fetchArchivedCallsFx.doneData, (state, response) => ({
    ...state,
    archivedCalls: response.data,
    archivedLoading: false,
    archivedError: null,
    archivedPagination: {
      ...state.archivedPagination,
      total: response.count,
      total_count: response.total_count,
      page: 1,
    },
  }))
  .on(fetchArchivedCallsFx.failData, (state, error) => ({
    ...state,
    archivedLoading: false,
    archivedError: error.message,
  }))
  .on(fetchCallGeneralDataRequestedFx.doneData, (state, response) => ({
    ...state,
    callGeneralData: response,
  }));

sample({
  clock: fetchCallsRequested,
  target: fetchCallsFx,
});

sample({
  clock: loadMoreCallsRequested,
  source: $callsStore,
  fn: (state, filters) => ({
    ...filters,
    page: state.pagination.page + 1,
  }),
  target: loadMoreCallsFx,
});

sample({
  clock: fetchCallDetailRequested,
  target: fetchCallDetailFx,
});

sample({
  clock: updateCallBookmark,
  target: updateCallBookmarkFx,
});
sample({
  clock: updateCallInterest,
  fn: (payload) => {
    // console.log(
      // "Sample: updateCallInterest event triggered, sending to effect:",
      // payload,
    // );
    return payload;
  },
  target: updateCallInterestFx,
});

sample({
  clock: createIdeaRequested,
  target: createIdeaFx,
});

sample({
  clock: fetchCallCommentsRequested,
  target: fetchCallCommentsFx,
});

sample({
  clock: createCommentRequested,
  target: createCommentFx,
});

// My calls samples
sample({
  clock: fetchDeclaredInterestCallsRequested,
  target: fetchDeclaredInterestCallsFx,
});

sample({
  clock: fetchBookmarkedCallsRequested,
  target: fetchBookmarkedCallsFx,
});

sample({
  clock: fetchArchivedCallsRequested,
  target: fetchArchivedCallsFx,
});

sample({
  clock: loadMoreDeclaredInterestCallsRequested,
  source: $callsStore,
  fn: (state, filters) => ({
    ...filters,
    page: state.declaredInterestPagination.page + 1,
  }),
  target: loadMoreDeclaredInterestCallsFx,
});

sample({
  clock: loadMoreBookmarkedCallsRequested,
  source: $callsStore,
  fn: (state, filters) => ({
    ...filters,
    page: state.bookmarkedPagination.page + 1,
  }),
  target: loadMoreBookmarkedCallsFx,
});

sample({
  clock: loadMoreArchivedCallsRequested,
  source: $callsStore,
  fn: (state, filters) => ({
    ...filters,
    page: state.archivedPagination.page + 1,
  }),
  target: loadMoreArchivedCallsFx,
});

// Auto-refresh comments after successful comment creation
sample({
  clock: createCommentFx.doneData,
  source: $callsStore,
  filter: (state) => Boolean(state.selectedCall?.id),
  fn: (state) => state.selectedCall!.id,
  target: fetchCallCommentsRequested,
});

// Call goals sample
sample({
  clock: fetchCallGoalsRequested,
  target: fetchCallGoalsFx,
});

// Count fetching samples
sample({
  clock: fetchAllCallsCountRequested,
  target: fetchAllCallsCountFx,
});

sample({
  clock: fetchForYouCallsCountRequested,
  target: fetchForYouCallsCountFx,
});

sample({
  clock: fetchCallGeneralDataRequested,
  target: fetchCallGeneralDataRequestedFx,
});
